import { modifier } from 'ember-modifier';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      modifier — behaviour of the NvTabs strip (spec docs/ui-nuova/components/tabs.md §5-§6):
 *              (1) roving focus for role=tablist: ←/→ move focus between the tabs (wrapping),
 *                  Home/End jump; Enter/Space activate natively (they are buttons: manual
 *                  activation, a panel may load data); when focus leaves the strip the Tab stop
 *                  returns to the selected tab (or the first one when none is selected);
 *              (2) the selected tab is scrolled into view INSIDE the strip on insert and on every
 *                  selection change (never the page: no scrollIntoView);
 *              (3) data-overflow-start / data-overflow-end flags drive the edge fade;
 *              (4) each label's text is copied into its data-text, which _tabs.scss draws hidden at
 *                  600 (::after) so the label is always as wide as its bold self: no shift on select.
 *   usage:     `{{nv-tablist roving=true}}` on the strip (nv-tabs.hbs); `roving` only for panel tabs.
 *   uses:      MutationObserver (aria-selected / aria-current / class of the tabs), ResizeObserver
 *   gotchas:   tabs are found as `.nv-tablist-tab` children of the strip (not nested strips:
 *              a strip inside a tab panel is a different element). The template binds tabindex
 *              from @selected; this modifier only writes it while the user moves with the arrows
 *              and when no tab is selected.
 */
const EDGE = 24;
var nvTablist = modifier((strip, positional, {
  roving = false
}) => {
  const tabs = () => [...strip.children].filter(el => el.classList.contains('nv-tablist-tab'));
  const selected = () => tabs().find(el => el.getAttribute('aria-selected') === 'true' || el.getAttribute('aria-current') === 'page' || el.classList.contains('is-selected') || el.classList.contains('active'));
  const reserve = () => {
    strip.querySelectorAll('.nv-tablist-label').forEach(label => {
      const text = (label.textContent || '').replace(/\s+/g, ' ').trim();
      if (label.dataset.text !== text) label.dataset.text = text;
    });
  };
  const flags = () => {
    const max = strip.scrollWidth - strip.clientWidth;
    strip.toggleAttribute('data-overflow-start', strip.scrollLeft > 1);
    strip.toggleAttribute('data-overflow-end', max > 1 && strip.scrollLeft < max - 1);
  };
  const reveal = () => {
    const tab = selected();
    if (tab && strip.scrollWidth > strip.clientWidth) {
      const left = tab.offsetLeft; // the strip is position: relative (_tabs.scss): offsets are strip-local
      const right = left + tab.offsetWidth;
      if (left < strip.scrollLeft + EDGE) strip.scrollLeft = Math.max(0, left - EDGE);else if (right > strip.scrollLeft + strip.clientWidth - EDGE) {
        strip.scrollLeft = right - strip.clientWidth + EDGE;
      }
    }
    flags();
  };
  const resetStop = () => {
    if (!roving) return;
    const all = tabs();
    const stop = selected() || all[0];
    all.forEach(el => el.setAttribute('tabindex', el === stop ? '0' : '-1'));
  };
  const onKeydown = event => {
    if (!roving || event.target.getAttribute('role') !== 'tab') return;
    const all = tabs();
    const index = all.indexOf(event.target);
    if (index < 0) return;
    let next = null;
    if (event.key === 'ArrowRight') next = all[(index + 1) % all.length];else if (event.key === 'ArrowLeft') next = all[(index - 1 + all.length) % all.length];else if (event.key === 'Home') next = all[0];else if (event.key === 'End') next = all[all.length - 1];
    if (!next) return;
    event.preventDefault();
    all.forEach(el => el.setAttribute('tabindex', el === next ? '0' : '-1'));
    next.focus();
  };
  const onFocusout = event => {
    if (!strip.contains(event.relatedTarget)) resetStop();
  };
  reserve();
  const frame = requestAnimationFrame(() => {
    lastSelected = selected();
    resetStop();
    reveal();
  });
  // the selected tab of the last pass: the strip scrolls only when the SELECTION changes (a count refresh or another
  // class change never snaps back a strip the user scrolled by hand); the Tab stop follows the selection even while
  // focus is inside the strip (a click after arrow moves left two stops otherwise)
  let lastSelected = null;
  const observer = new MutationObserver(() => {
    reserve();
    const current = selected();
    if (current !== lastSelected) {
      lastSelected = current;
      resetStop();
      reveal();
    } else {
      flags();
    }
  });
  observer.observe(strip, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['aria-selected', 'aria-current', 'class']
  });
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(flags) : null;
  resize?.observe(strip);
  strip.addEventListener('scroll', flags, {
    passive: true
  });
  strip.addEventListener('keydown', onKeydown);
  strip.addEventListener('focusout', onFocusout);
  return () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
    resize?.disconnect();
    strip.removeEventListener('scroll', flags);
    strip.removeEventListener('keydown', onKeydown);
    strip.removeEventListener('focusout', onFocusout);
  };
});

export { nvTablist as default };
//# sourceMappingURL=nv-tablist.js.map
